import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Alert, AlertDescription } from "../ui/alert"
import { Mail, ArrowLeft, CheckCircle, Brain, Eye, EyeOff } from "lucide-react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { apiService } from "../../lib/api"

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

const resetPasswordSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export function ForgotPasswordForm() {
  // Check for token immediately on mount (before state initialization)
  const initialToken = typeof window !== 'undefined' 
    ? new URLSearchParams(window.location.search).get('token')
    : null
  
  const [step, setStep] = useState<'request' | 'reset'>(initialToken ? 'reset' : 'request')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [resetToken, setResetToken] = useState(initialToken || "")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const {
    register: registerForgot,
    handleSubmit: handleForgotSubmit,
    formState: { errors: forgotErrors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    formState: { errors: resetErrors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const [resetLink, setResetLink] = useState<string>("")

  const onForgotPasswordSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    setError("")
    setSuccess("")
    setResetLink("")
    
    try {
      const response = await apiService.forgotPassword(data.email) as any
      if (response.resetLink) {
        // Development mode - show the reset link
        setResetLink(response.resetLink)
        setSuccess("Password reset link generated. Click the link below to reset your password.")
      } else {
        setSuccess(response.message || "Password reset link has been sent to your email address.")
      }
    } catch (err: any) {
      console.error("❌ ForgotPasswordForm: Request failed:", err)
      // Check if it's a 404 (email not found) or contains email_not_found
      if (err.status === 404 || err.body?.error === 'email_not_found' || 
          err.message?.includes('email_not_found') || 
          err.message?.includes('No account found')) {
        setError("No account found with this email address. Please check your email and try again.")
      } else {
        setError(err.message || "Failed to send password reset link. Please try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onResetPasswordSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true)
    setError("")
    setSuccess("")
    
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token') || resetToken
      
      if (!token) {
        setError("Reset token is missing. Please use the link from your email.")
        setIsLoading(false)
        return
      }

      await apiService.resetPassword(token, data.password)
      setSuccess("Your password has been reset successfully. You can now login with your new password.")
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/auth/login")
      }, 2000)
    } catch (err: any) {
      console.error("❌ ForgotPasswordForm: Reset failed:", err)
      setError(err.message || "Failed to reset password. The link may have expired. Please request a new one.")
    } finally {
      setIsLoading(false)
    }
  }

  // Check if we have a token in URL (for reset step) - runs immediately and on URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const token = urlParams.get('token')
    console.log('🔍 ForgotPasswordForm: Checking for reset token in URL:', { 
      token: token ? `${token.substring(0, 10)}...` : null, 
      search: location.search, 
      pathname: location.pathname,
      fullUrl: window.location.href 
    })
    
    if (token && token.length > 0) {
      console.log('✅ ForgotPasswordForm: Token found, switching to reset step')
      setResetToken(token)
      setStep('reset')
      setError("") // Clear any previous errors
      setSuccess("") // Clear any previous success messages
    } else if (!token) {
      // Only reset to request step if there's definitely no token
      console.log('ℹ️ ForgotPasswordForm: No token found, showing request form')
      if (step !== 'request') {
        setStep('request')
      }
    }
  }, [location.search, location.pathname])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 to-blue-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Brain className="w-6 h-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {step === 'request' ? 'Forgot Password' : 'Reset Password'}
          </CardTitle>
          <CardDescription>
            {step === 'request' 
              ? "Enter your email address and we'll send you a link to reset your password"
              : 'Enter your new password'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {success}
                {resetLink && (
                  <div className="mt-3 p-3 bg-white rounded border border-green-200">
                    <p className="text-xs font-medium text-green-900 mb-2">Development Mode - Reset Link:</p>
                    <a 
                      href={resetLink} 
                      className="text-xs text-blue-600 hover:underline break-all"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {resetLink}
                    </a>
                    <p className="text-xs text-green-700 mt-2">Click the link above to reset your password</p>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {step === 'request' ? (
            <form 
              onSubmit={(e) => {
                e.preventDefault()
                handleForgotSubmit(onForgotPasswordSubmit)(e)
              }} 
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className="pl-10 border border-gray-200"
                    {...registerForgot("email")}
                    disabled={isLoading}
                  />
                </div>
                {forgotErrors.email && (
                  <p className="text-sm text-destructive">{forgotErrors.email.message}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Link"}
              </Button>
              
              <div className="flex items-center justify-center">
                <Link
                  to="/auth/login"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </Link>
              </div>
            </form>
          ) : (
            <form 
              onSubmit={(e) => {
                e.preventDefault()
                handleResetSubmit(onResetPasswordSubmit)(e)
              }} 
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your new password"
                    className="border border-gray-200 pr-10"
                    {...registerReset("password")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {resetErrors.password && (
                  <p className="text-sm text-destructive">{resetErrors.password.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your new password"
                    className="border border-gray-200 pr-10"
                    {...registerReset("confirmPassword")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {resetErrors.confirmPassword && (
                  <p className="text-sm text-destructive">{resetErrors.confirmPassword.message}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
              
              <div className="flex items-center justify-center">
                <Link
                  to="/auth/login"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

